import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';
import type React from 'react';
import { useEffect, useState } from 'react';
import { StandardTable } from '../../components/common/UI/StandardTable';
import { usePricing } from '../../contexts/PricingContext';
import { useTeamLimits } from '../../hooks/useTeamLimits';
import { EstimatedCostChip } from '../common/EstimatedCostChip';
import SupportDocumentTable from './SupportDocumentTable';
import TemplateSelector from './TemplateSelector';
import type { LoadingStates, SupportDoc, UploadedAt } from './types';
import {
  extractProgressFromStatus,
  formatGenerationStatusText,
  formatUploadedDate,
  getDropzoneBoxStyle,
  getLoadingKey,
  toDate,
} from './utils';

const isStatusProcessing = (status: string): boolean => {
  const lowerStatus = status.toLowerCase();
  if (lowerStatus === '' || lowerStatus === 'idle') {
    return false;
  }
  if (lowerStatus.includes('complete') || lowerStatus.includes('success')) {
    return false;
  }
  if (lowerStatus.includes('error') || lowerStatus.includes('failed')) {
    return false;
  }
  return true;
};

export interface GeneratedCV {
  name: string;
  uri: string;
  generatedAt?: Date | string;
}

interface GenerateCVsSectionProps {
  tenderId: string;
  usedCount?: number;
  // Input CVs (from user_docs categorized as 'cv')
  inputCVs: SupportDoc[];
  // Generated CVs (from cvGeneration service)
  generatedCVs: GeneratedCV[];
  isGenerating: boolean;
  cvGenerationStatus: string;
  cvServiceStartedAt?: UploadedAt | undefined;
  loadingStates: LoadingStates;

  // Handlers
  onGenerate: () => void;
  onGenerateSingle: (doc: SupportDoc) => void;
  onDownloadInput: (doc: SupportDoc) => void;
  onDownloadGenerated: (uri: string, name: string) => void;
  onDeleteInput: (doc: SupportDoc) => void;
  onResetCVService: () => void;
  onResetSingleCV?: (doc: SupportDoc) => void;
  onReportSingleCVError?: (doc: SupportDoc) => void;

  // Upload Props
  isDragging: boolean;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onFileUpload: (file: File) => void;
  processingFiles: string[];

  isDraftGenerated?: boolean;
  selectedTemplates?: Record<string, string>;
  onTemplateChange?: (type: string, templateId: string) => void;
  // biome-ignore lint/suspicious/noExplicitAny: allow generic template structure
  availableTemplates?: any[];
}

const getRegenButtonStyle = (theme: Theme, enabled: boolean) => ({
  backgroundColor: enabled ? theme.palette.greyContrast : theme.palette.greyLight,
  color: enabled ? theme.palette.greyLight : theme.palette.greyContrast,
  border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
  fontSize: '1rem',
  fontWeight: 600,
  opacity: enabled ? 1 : 0.7,
  boxShadow: theme.shadows[2],
  height: '36px',
  minHeight: '36px',
  padding: theme.spacing(0.5, 2),
  '&:hover': enabled
    ? {
        backgroundColor: theme.palette.bidlogiq?.brick || '#b73f2a',
        color: '#fff',
        boxShadow: theme.shadows[4],
      }
    : {
        backgroundColor: theme.palette.greyLight,
        boxShadow: theme.shadows[4],
      },
  '&.Mui-disabled': {
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : theme.palette.greyLight,
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : theme.palette.greyContrast,
    border:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.12)'
        : `1px solid ${theme.palette.bidlogiq?.orange}`,
  },
  width: '100%',
});

const getDownloadButtonStyle = (theme: Theme, enabled: boolean) => ({
  backgroundColor: enabled ? theme.palette.bidlogiq?.brick : theme.palette.greyLight,
  color: enabled ? theme.palette.bidlogiq?.white : theme.palette.greyContrast,
  border: enabled ? 'none' : `1px solid ${theme.palette.greyContrast}`,
  fontSize: '1rem',
  fontWeight: 600,
  opacity: enabled ? 1 : 0.7,
  boxShadow: theme.shadows[2],
  height: 'auto',
  minHeight: '36px',
  padding: theme.spacing(0.5, 2),
  '&:hover': {
    backgroundColor: enabled ? theme.palette.bidlogiq?.rust : theme.palette.greyLight,
    boxShadow: theme.shadows[4],
  },
  '&.Mui-disabled': {
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : theme.palette.greyLight,
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : theme.palette.greyContrast,
    border:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.12)'
        : `1px solid ${theme.palette.bidlogiq?.orange}`,
  },
  width: '100%',
  justifyContent: 'center',
  '& .MuiButton-label': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    lineHeight: 1.2,
  },
});

const GenerateCVsSection: React.FC<GenerateCVsSectionProps> = ({
  tenderId,
  usedCount = 0,
  inputCVs,
  generatedCVs,
  isGenerating: _isGeneratingProp,
  cvGenerationStatus,
  cvServiceStartedAt,
  loadingStates,
  onGenerate,
  onGenerateSingle,
  onDownloadGenerated,
  onDeleteInput,
  onResetCVService,
  onResetSingleCV,
  onReportSingleCVError,
  isDragging,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  onFileUpload,
  processingFiles,
  isDraftGenerated,
  selectedTemplates = {},
  onTemplateChange,
  availableTemplates = [],
}) => {
  const theme = useTheme();
  const { pricingSettings } = usePricing();
  const [resetCooldown, setResetCooldown] = useState<number>(0);
  const [reportedDocs, setReportedDocs] = useState<Set<string>>(new Set());

  const { checkLimit } = useTeamLimits();

  const isProcessing = isStatusProcessing(cvGenerationStatus || '');
  const combinedReport = generatedCVs.find((cv) => cv.name.toLowerCase().includes('combined'));

  const [maxProgress, setMaxProgress] = useState(0);
  useEffect(() => {
    const currentProgress = extractProgressFromStatus(cvGenerationStatus) ?? 0;
    if (!isProcessing) {
      setMaxProgress(0);
    } else {
      setMaxProgress((prev) => Math.max(prev, currentProgress));
    }
  }, [isProcessing, cvGenerationStatus]);

  const handleGenerateClick = () => {
    onGenerate();
  };

  const {
    remaining: remainingQuota,
    isLimitReached,
    isUnlimited,
  } = checkLimit('adHocDocs', usedCount);
  const hasQuota = !isLimitReached;

  useEffect(() => {
    const checkCooldown = () => {
      if (!cvServiceStartedAt || !isProcessing) {
        setResetCooldown(0);
        return;
      }

      const startedAt = toDate(cvServiceStartedAt);

      const now = new Date();
      // Fixed 15 minutes cooldown for global service
      const cooldownMs = 15 * 60 * 1000;
      const elapsed = now.getTime() - startedAt.getTime();

      if (elapsed < cooldownMs) {
        setResetCooldown(Math.ceil((cooldownMs - elapsed) / 60000));
      } else {
        setResetCooldown(0);
      }
    };

    checkCooldown();
    const interval = setInterval(checkCooldown, 60000);
    return () => clearInterval(interval);
  }, [cvServiceStartedAt, isProcessing]);

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Left Column: Instructions and Template */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              sx={{ fontSize: '1rem', color: 'text.secondary' }}
              component="div"
            >
              <strong>Workflow:</strong>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                <li>
                  <strong>Upload CVs:</strong> Select your team in Step 3, and/or upload any
                  additional CVs/Resumes here.
                </li>
                <li>
                  <strong>Generate Draft first:</strong> You must generate your draft proposal
                  before generating CVs. This ensures the CVs are tailored perfectly to match the
                  exact promises and context of your proposal.
                </li>
                <li>
                  <strong>CV Output Template:</strong> Select the structure and format for your
                  generated CVs using the dropdown below.
                </li>
                <li>
                  <strong>Generate CVs:</strong> Click the button to the right to generate optimized
                  CVs for this tender. Alternatively, generate them one at a time using the buttons
                  in the list below.
                </li>
                <li>
                  <strong>Review:</strong> Download the combined version to the right; or download
                  them individually from the list below.
                </li>
              </ul>
            </Typography>
          </Box>

          <Box sx={{ mt: 2, mb: 1 }}>
            <TemplateSelector
              selectedTemplates={selectedTemplates}
              onTemplateChange={onTemplateChange || (() => undefined)}
              availableTemplates={availableTemplates}
              type="cv"
              label="CV Output Template"
              title="Select CV Template"
            />
          </Box>

          {hasQuota && (
            <Box sx={{ mt: 2 }}>
              <Tooltip
                title="Exact billing depends on the length and complexity of the tender documents and provided context."
                slotProps={{ tooltip: { sx: { fontSize: '0.875rem', p: 1 } } }}
              >
                <EstimatedCostChip
                  estimate={
                    pricingSettings?.estimates?.adhoc_doc_generation || 'Est. $15.00 – $50.00'
                  }
                  sx={{ fontSize: '0.875rem' }}
                />
              </Tooltip>
            </Box>
          )}
        </Grid>

        {/* Right Column: Upload and Global Actions */}
        <Grid size={{ xs: 12, md: 6 }}>
          {/* Shared Dropzone */}
          <Box
            sx={getDropzoneBoxStyle(theme, isDragging)}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <input
              type="file"
              id="cv-upload"
              multiple
              accept=".pdf,.docx,.txt,.md,.csv,.xls,.xlsx"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files) {
                  for (const file of Array.from(e.target.files)) {
                    onFileUpload(file);
                  }
                }
              }}
            />
            <label htmlFor="cv-upload" style={{ width: '100%', cursor: 'pointer' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {loadingStates[getLoadingKey(tenderId, 'upload', 'cv')] ? (
                  <CircularProgress size={24} sx={{ mb: 1 }} />
                ) : (
                  <UploadFileIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                )}
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  sx={{ fontSize: '1rem' }}
                >
                  {loadingStates[getLoadingKey(tenderId, 'upload', 'cv')]
                    ? 'Uploading...'
                    : 'Drag & Drop CVs here (PDF, DOCX, TXT, MD - Max 5MB)'}
                </Typography>
              </Box>
            </label>
          </Box>

          {/* Processing Files List */}
          {processingFiles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
                Processing Uploads:
              </Typography>
              <StandardTable
                columns={[
                  {
                    id: 'fileName',
                    label: 'File Name',
                    render: (fileName) => fileName,
                  },
                  {
                    id: 'progress',
                    label: '',
                    align: 'right',
                    render: () => <CircularProgress size={16} />,
                  },
                ]}
                data={processingFiles}
                keyExtractor={(fileName) => fileName}
                size="small"
                stickyHeader={false}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', mt: 3, gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip
                title={
                  !isDraftGenerated
                    ? 'Generate a draft to enable CV generation'
                    : !hasQuota
                      ? 'Insufficient CV generation quota'
                      : ''
                }
                arrow
                placement="top"
              >
                <Box sx={{ flexGrow: 1 }}>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleGenerateClick}
                    disabled={
                      isProcessing || inputCVs.length === 0 || !hasQuota || !isDraftGenerated
                    }
                    sx={getRegenButtonStyle(
                      theme,
                      !isProcessing && inputCVs.length > 0 && hasQuota && !!isDraftGenerated
                    )}
                  >
                    {isProcessing ? (
                      <>
                        <CircularProgress
                          size={16}
                          color="inherit"
                          sx={{ mr: 1, color: theme.palette.bidlogiq?.orange }}
                        />
                        {extractProgressFromStatus(cvGenerationStatus) !== undefined
                          ? `${formatGenerationStatusText(cvGenerationStatus)} ${maxProgress}%`
                          : cvGenerationStatus || 'Generating...'}
                      </>
                    ) : generatedCVs.length > 0 ? (
                      `Regenerate All CVs${isUnlimited ? '' : ` (${remainingQuota} left)`}`
                    ) : (
                      `Generate All CVs${isUnlimited ? '' : ` (${remainingQuota} left)`}`
                    )}
                  </Button>
                </Box>
              </Tooltip>
            </Box>

            <Button
              variant="outlined"
              onClick={() =>
                combinedReport && onDownloadGenerated(combinedReport.uri, combinedReport.name)
              }
              disabled={
                !combinedReport ||
                !!loadingStates[
                  getLoadingKey(tenderId, 'download', `cv_generated_${combinedReport?.name}`)
                ]
              }
              startIcon={
                loadingStates[
                  getLoadingKey(tenderId, 'download', `cv_generated_${combinedReport?.name}`)
                ] ? (
                  <CircularProgress size={18} sx={{ color: 'bidlogiq.orange' }} />
                ) : (
                  <DownloadIcon />
                )
              }
              sx={getDownloadButtonStyle(theme, !!combinedReport)}
            >
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.2, fontWeight: 600, textAlign: 'center' }}
              >
                Download Combined CVs
                {combinedReport && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ ml: 0.5, fontSize: '0.75rem', fontWeight: 400, opacity: 0.9 }}
                  >
                    {`, ${formatUploadedDate(combinedReport.generatedAt)}`}
                  </Typography>
                )}
                {' (docx)'}
              </Typography>
            </Button>

            {inputCVs.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flexGrow: 1, fontSize: '0.875rem' }}
                >
                  Generating {inputCVs.length} CVs can take up to{' '}
                  {Math.max(1, inputCVs.length) * 15} minutes. Press the reset button if it is
                  stuck.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={onResetCVService}
                  disabled={resetCooldown > 0}
                  sx={{
                    borderColor: 'text.error',
                    color: 'text.error',
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                  }}
                >
                  {resetCooldown > 0 ? `Reset (${resetCooldown}m)` : 'Reset'}
                </Button>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Full Width Table Section */}
      <Box sx={{ width: '100%' }}>
        <SupportDocumentTable
          title="Project Team"
          documents={inputCVs}
          emptyMessage="No CVs uploaded yet."
          renderActions={(doc) => {
            const isDeleting =
              loadingStates[
                `delete_cv_${doc.originalFilename || doc.name || doc.filename || doc.id}`
              ];
            const rowStatus = doc.generationStatus || 'idle';

            let generatedUri = doc.generatedFileUri;
            if (!generatedUri) {
              const rawStem =
                (doc.originalFilename || doc.name || doc.filename || '').split('.')[0] || '';
              const safeStem = rawStem
                .replace(/[^a-zA-Z0-9 _-]/g, '')
                .trim()
                .replace(/ /g, '_');
              if (safeStem) {
                const match = generatedCVs.find(
                  (gcv) => gcv.name.includes(safeStem) && !gcv.name.includes('combined_report')
                );
                if (match) {
                  generatedUri = match.uri;
                }
              }
            }

            const isRowProcessing =
              rowStatus === 'processing' ||
              rowStatus === 'generating' ||
              rowStatus === 'initializing';

            const isRowSuccess =
              rowStatus === 'success' || rowStatus === 'complete' || !!generatedUri;

            const isGlobalError =
              cvGenerationStatus?.toLowerCase().includes('error') ||
              cvGenerationStatus?.toLowerCase().includes('failed');

            const isRowError =
              rowStatus === 'error' || rowStatus === 'failed' || (isRowProcessing && isGlobalError);

            let canResetRow = false;
            const refDate = doc.startedAt || doc.updatedAt || doc.lastModified;

            if (rowStatus === 'initializing' || isRowError) {
              canResetRow = true;
            } else if (isRowProcessing && refDate) {
              const startedAt = toDate(refDate);
              const now = new Date();
              const diff = now.getTime() - startedAt.getTime();
              const cooldownMs = 15 * 60 * 1000;
              if (diff > cooldownMs) {
                canResetRow = true;
              }
            } else if (isRowProcessing) {
              canResetRow = false;
            }

            return (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  alignItems: 'center',
                }}
              >
                <Tooltip
                  title={
                    !isDraftGenerated
                      ? 'Generate a draft to enable CV generation'
                      : !hasQuota
                        ? 'Insufficient CV generation quota'
                        : ''
                  }
                  arrow
                  placement="top"
                >
                  <Box>
                    <Button
                      type="button"
                      variant="contained"
                      size="small"
                      onClick={() => onGenerateSingle(doc)}
                      disabled={
                        (isRowProcessing && !isRowError) ||
                        (isProcessing && !isGlobalError) ||
                        !hasQuota ||
                        !isDraftGenerated ||
                        (!!doc.conversionStatus &&
                          doc.conversionStatus !== 'success' &&
                          doc.generationStatus !== 'success')
                      }
                      sx={{
                        backgroundColor: isRowError
                          ? theme.palette.error.main
                          : theme.palette.bidlogiq?.brick || theme.palette.primary.main,
                        color: 'white',
                        '&:hover': {
                          backgroundColor: isRowError
                            ? theme.palette.error.dark
                            : theme.palette.bidlogiq?.brick || theme.palette.primary.dark,
                          opacity: 0.9,
                        },
                        fontSize: '0.875rem',
                        minWidth: 'auto',
                        px: 1.5,
                        textTransform: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&.Mui-disabled': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.12)'
                              : theme.palette.grey[300],
                          color:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.3)'
                              : theme.palette.text.disabled,
                        },
                      }}
                    >
                      {isRowProcessing && !isRowError ? (
                        <>
                          <CircularProgress size={14} color="inherit" />
                          {rowStatus === 'initializing' ? 'Initializing...' : 'Generating...'}
                        </>
                      ) : isRowError ? (
                        'Retry'
                      ) : isRowSuccess ? (
                        'Regenerate'
                      ) : (
                        'Generate'
                      )}
                    </Button>
                  </Box>
                </Tooltip>

                {isRowProcessing && !isRowError && onResetSingleCV && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => onResetSingleCV(doc)}
                    disabled={!canResetRow}
                    sx={{
                      borderColor: 'text.error',
                      color: 'text.error',
                      fontSize: '0.7rem',
                      px: 1,
                      minWidth: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Reset
                  </Button>
                )}

                {isRowError &&
                  !isRowProcessing &&
                  onReportSingleCVError &&
                  !reportedDocs.has(doc.originalFilename || '') && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      onClick={() => {
                        onReportSingleCVError(doc);
                        setReportedDocs((prev) => new Set(prev).add(doc.originalFilename || ''));
                      }}
                      sx={{
                        fontSize: '0.7rem',
                        px: 1,
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Report Issue
                    </Button>
                  )}

                {isRowError && reportedDocs.has(doc.originalFilename || '') && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem', fontStyle: 'italic', px: 1 }}
                  >
                    Reported
                  </Typography>
                )}

                <IconButton
                  onClick={() =>
                    generatedUri &&
                    onDownloadGenerated(
                      generatedUri,
                      `QCV_${tenderId.split('_').pop()}_${doc.originalFilename?.replace(/\.[^/.]+$/, '')}.docx`
                    )
                  }
                  disabled={
                    !generatedUri ||
                    !!loadingStates[
                      getLoadingKey(
                        tenderId,
                        'download',
                        `cv_generated_QCV_${tenderId.split('_').pop()}_${doc.originalFilename?.replace(/\.[^/.]+$/, '')}.docx`
                      )
                    ]
                  }
                  title={generatedUri ? 'Download Optimized CV' : 'Generate to download'}
                  size="small"
                  sx={{
                    color: generatedUri
                      ? theme.palette.bidlogiq?.brick || theme.palette.primary.main
                      : theme.palette.action.disabled,
                  }}
                >
                  {loadingStates[
                    getLoadingKey(
                      tenderId,
                      'download',
                      `cv_generated_QCV_${tenderId.split('_').pop()}_${doc.originalFilename?.replace(/\.[^/.]+$/, '')}.docx`
                    )
                  ] ? (
                    <CircularProgress size={18} />
                  ) : (
                    <DownloadIcon sx={{ fontSize: 28 }} />
                  )}
                </IconButton>

                <IconButton
                  onClick={() => onDeleteInput(doc)}
                  disabled={!!isDeleting || !!doc.isLibraryItem}
                  title={
                    doc.isLibraryItem
                      ? 'Cannot delete CVs sourced from the main library here'
                      : 'Delete CV'
                  }
                  size="small"
                >
                  {isDeleting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DeleteIcon sx={{ fontSize: 28 }} />
                  )}
                </IconButton>
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

export default GenerateCVsSection;
